import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { getOutfitPreviewUri, Outfit } from "../../models/Outfit";
import { OutfitPickerItem, PreviewGender } from "./outfitPickerModel";
import "./OutfitPicker.css";

/**
 * The 4:5 stage an outfit render stands on, shared by the tiles, the details
 * pane and the field. Draws the render, the actor's own outfit (faded) for
 * Default, the shuffle glyph for Random, or a "no preview" placeholder.
 */
export default function OutfitStage(props: {
  item: OutfitPickerItem;
  gender: PreviewGender;
  /** The actor's own outfit, shown faded on the Default stage. */
  ownOutfit?: Outfit;
  /** Small stages (the field thumbnail) drop the caption text. */
  compact?: boolean;
  /** Fill the parent (absolutely positioned) instead of a 4:5 box. */
  fill?: boolean;
  className?: string;
  children?: React.ReactNode;
}) {
  const { item, gender, ownOutfit, compact, fill, className, children } = props;
  const { t } = useTranslation();

  let content: React.ReactNode;
  let plain = false;
  if (item.kind === "random") {
    plain = true;
    content = (
      <div className="flex h-full items-center justify-center text-creator-text-sub">
        <ShuffleIcon sx={{ fontSize: compact ? 20 : 40 }} />
      </div>
    );
  } else {
    const outfit = item.kind === "outfit" ? item.outfit : ownOutfit;
    const uri = getOutfitPreviewUri(outfit, gender);
    if (uri) {
      content = (
        <img
          src={uri}
          alt=""
          draggable={false}
          className={classNames({ "opacity-[0.55]": item.kind === "default" })}
        />
      );
    } else {
      plain = true;
      content = (
        <div className="flex h-full flex-col items-center justify-center gap-[8px] text-creator-text-sub text-[12px]">
          <PersonOutlineIcon sx={{ fontSize: compact ? 20 : 32 }} />
          {!compact && t("outfit_picker.no_preview")}
        </div>
      );
    }
  }

  return (
    <div
      className={classNames(
        "outfitpicker-stage",
        { "outfitpicker-stage--plain": plain && !fill, "outfitpicker-stage--fill": fill },
        className
      )}
    >
      {content}
      {children}
    </div>
  );
}
